import React, { useState } from 'react';
import { Button, Avatar, Tag, Modal, Form, Select, message, Divider, Grid } from 'antd';
import {
    UserOutlined,
    CloseCircleOutlined,
    SwapOutlined,
    FileTextOutlined,
    MedicineBoxOutlined,
    HomeOutlined,
    DollarOutlined,
    CalendarOutlined,
    TeamOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { useBreakpoint } = Grid;

const getTargetTypeDisplay = (targetType) => {
    const types = {
        0: { label: 'General', icon: <FileTextOutlined />, color: 'default' },
        1: { label: 'Service', icon: <DollarOutlined />, color: 'green' },
        2: { label: 'Clinic', icon: <HomeOutlined />, color: 'green' },
        3: { label: 'Doctor', icon: <MedicineBoxOutlined />, color: 'green' }
    };
    return types[targetType] || types[0];
};

const ChatHeader = ({
    ovr,
    getStaffInfo,
    getStatusBadge,
    onCloseOvr,
    onMoveOvr,
    isClosing,
    isMoving,
    role,
    staffData,
    staffLoading,
    onBack // provided on mobile only
}) => {
    const screens = useBreakpoint();
    const isMobile = screens.md === false;

    const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
    const [moveForm] = Form.useForm();

    if (!ovr) return null;

    const statusBadge = getStatusBadge(ovr?.status);
    const targetTypeInfo = getTargetTypeDisplay(ovr?.targetType);
    const adminInfo = getStaffInfo(ovr?.adminUserId);
    const secretaryInfo = getStaffInfo(ovr?.secretaryUserId);

    const handleMoveOvr = async () => {
        try {
            const values = await moveForm.validateFields();
            await onMoveOvr(ovr?.id, values.newAdminUserId, values.newSecretaryUserId);
            setIsMoveModalVisible(false);
            moveForm.resetFields();
            message.success('OVR moved successfully');
        } catch (err) {
            console.error('Move OVR error:', err);
        }
    };

    const openMoveModal = () => {
        moveForm.setFieldsValue({
            newAdminUserId: ovr?.adminUserId,
            newSecretaryUserId: ovr?.secretaryUserId
        });
        setIsMoveModalVisible(true);
    };

    const closeMoveModal = () => {
        setIsMoveModalVisible(false);
        moveForm.resetFields();
    };

    return (
        <>
            <div className="bg-white border-b border-gray-200 shadow-sm">
                {/* Top Section - Title and Actions */}
                <div className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2 lg:gap-4">
                        {/* Left: Back + Title + metadata */}
                        <div className="flex items-start gap-1 min-w-0 flex-1">
                            {onBack && (
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={onBack}
                                    className="shrink-0 -ml-2"
                                />
                            )}
                            <div className="min-w-0 flex-1">
                                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate m-0 mb-1">
                                    {ovr?.title}
                                </h1>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                                    <div className="flex items-center gap-1">
                                        <CalendarOutlined />
                                        <span>Created {dayjs(`${ovr?.createdAt}Z`).format('MMM DD, YYYY')}</span>
                                    </div>
                                    {ovr?.updatedAt && (
                                        <div className="flex items-center gap-1">
                                            <span>•</span>
                                            <span>Updated {dayjs(`${ovr?.updatedAt}Z`).fromNow()}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right: Tags + Action Buttons */}
                        <div className="flex flex-wrap items-center gap-2">
                            <Tag
                                icon={targetTypeInfo.icon}
                                color={targetTypeInfo.color}
                                className="m-0 px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm flex items-center gap-1"
                            >
                                {targetTypeInfo.label}
                            </Tag>
                            <Tag
                                color={statusBadge.color}
                                className="m-0 px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium"
                            >
                                {statusBadge.text}
                            </Tag>
                            {ovr?.status !== 2 && Number(role) === 3 && (
                                <>
                                    <Button
                                        type="default"
                                        size={isMobile ? 'small' : 'middle'}
                                        icon={<SwapOutlined />}
                                        onClick={openMoveModal}
                                        loading={isMoving}
                                        className="flex items-center gap-1"
                                    >
                                        Move
                                    </Button>
                                    <Button
                                        danger
                                        size={isMobile ? 'small' : 'middle'}
                                        icon={<CloseCircleOutlined />}
                                        onClick={() => onCloseOvr(ovr?.id)}
                                        loading={isClosing}
                                        className="flex items-center gap-1"
                                    >
                                        Close
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <Divider className="m-0" />

                {/* Bottom Section - Team Members */}
                <div className="px-3 sm:px-6 py-2 sm:py-3 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2">
                        <div className="flex items-center gap-2 text-sm">
                            <TeamOutlined className="text-gray-400" />
                            <span className="text-gray-500 font-medium">Team:</span>
                        </div>

                        {/* Admin */}
                        <div className="flex items-center gap-2 min-w-0">
                            <Avatar size={28} icon={<UserOutlined />} className="bg-[#298d9f] shrink-0" />
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs font-semibold text-gray-800 truncate">
                                    {adminInfo?.name || 'Unknown Admin'}
                                </span>
                                <span className="text-xs text-gray-500">Admin</span>
                            </div>
                        </div>

                        {/* Secretary */}
                        {ovr?.secretaryUserId && secretaryInfo && (
                            <>
                                <Divider type="vertical" className="h-8 hidden sm:block" />
                                <div className="flex items-center gap-2 min-w-0">
                                    <Avatar size={28} icon={<UserOutlined />} className="bg-green-600 shrink-0" />
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-xs font-semibold text-gray-800 truncate">
                                            {secretaryInfo?.name || 'Unknown Secretary'}
                                        </span>
                                        <span className="text-xs text-gray-500">Secretary</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Move OVR Modal */}
            <Modal
                title={
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                            <SwapOutlined className="text-[#298d9f] text-lg" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 m-0">Move OVR</h3>
                            <p className="text-xs text-gray-500 m-0 font-normal">Reassign this OVR to different team members</p>
                        </div>
                    </div>
                }
                open={isMoveModalVisible}
                onCancel={closeMoveModal}
                centered
                width="min(600px, calc(100vw - 24px))"
                footer={[
                    <Button key="cancel" size="large" onClick={closeMoveModal}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        size="large"
                        loading={isMoving}
                        onClick={handleMoveOvr}
                        className="bg-[#298d9f] hover:bg-[#236d7c]"
                    >
                        Move OVR
                    </Button>,
                ]}
                destroyOnClose
            >
                <Form form={moveForm} layout="vertical" className="mt-4 sm:mt-6">
                    <Form.Item
                        name="newAdminUserId"
                        label={<span className="text-sm font-semibold text-gray-700">New Admin</span>}
                        rules={[{ required: true, message: 'Please select a new admin' }]}
                    >
                        <Select
                            placeholder="Select new admin"
                            showSearch
                            size="large"
                            loading={staffLoading}
                            filterOption={(input, option) =>
                                option.children.props.children[1].props.children
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {staffData?.staff
                                ?.filter(s => s.role === 3 || s.roleStr === 'CenterAdmin')
                                .map(admin => (
                                    <Option key={admin.userId} value={admin.userId}>
                                        <div className="flex items-center gap-3 py-1">
                                            <Avatar size={32} icon={<UserOutlined />} className="bg-[#298d9f]" />
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-900">{admin.name}</span>
                                            </div>
                                        </div>
                                    </Option>
                                ))
                            }
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="newSecretaryUserId"
                        label={<span className="text-sm font-semibold text-gray-700">New Secretary</span>}
                        rules={[{ required: true, message: 'Please select a new secretary' }]}
                    >
                        <Select
                            placeholder="Select new secretary"
                            showSearch
                            size="large"
                            loading={staffLoading}
                            filterOption={(input, option) =>
                                option.children.props.children[1].props.children
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {staffData?.staff
                                ?.filter(s => s.role === 4 || s.roleStr === 'Secretary')
                                .map(secretary => (
                                    <Option key={secretary.userId} value={secretary.userId}>
                                        <div className="flex items-center gap-3 py-1">
                                            <Avatar size={32} icon={<UserOutlined />} className="bg-green-600" />
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-900">{secretary.name}</span>
                                            </div>
                                        </div>
                                    </Option>
                                ))
                            }
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ChatHeader;